/**
 * Cached graph interface that integrates with the Graph hooks API.
 */
import { useGraphCacheStore } from '@tunasong/graph-cache'
import { includeElement, logger, type FilterFunc } from '@tunasong/models'
import {
  isAudio,
  isPersisted,
  type Audio,
  type ElementType,
  type Entity,
  type EntityType,
  type Persisted,
} from '@tunasong/schemas'
import { useEffect } from 'react'
import type { EntityUpdateFn, GraphHooks, UseChildren } from '../graph-types.js'
import { usePastCollaborators } from '../impl/use-past-collaborators.js'
import { childFilter, getLatestEntry } from '../util.js'
import { useCurrentUser } from '@tunasong/redux'
import invariant from 'tiny-invariant'

export const createCachedGraph = <T extends GraphHooks>(graphHooks: T) => {
  const useEntity = (id?: string | null) => {
    const { entityCache, upsertCache } = useGraphCacheStore()
    const status = graphHooks.useEntity(id)

    useEffect(() => {
      if (!(id && status.isSuccess && status.entity)) {
        return
      }
      upsertCache(status.entity)
    }, [id, status.entity, status.isSuccess, upsertCache])

    return { ...status, entity: getLatestEntry({ id, candidate: status.entity, cache: entityCache }) }
  }

  const useEntityUpdate = <E extends Entity = Entity>(props: {
    upsert?: boolean
    debounceDelay?: number
  }): EntityUpdateFn<E> => {
    const apiUpdate = graphHooks.useEntityUpdate(props)
    const { partialUpdateCache, getEntry } = useGraphCacheStore()

    return (entityId, update) => {
      // Resolve functional updates against the freshest cache entry
      const cacheEntry = getEntry(entityId)
      if (typeof update === 'function' && !cacheEntry) {
        logger.warn('Functional updater called before cache is populated for entity', entityId)
      }
      const resolved = typeof update === 'function' ? update((cacheEntry ?? { id: entityId }) as unknown as E) : update

      // Update the cache (if it exists)
      partialUpdateCache(entityId, {
        ...resolved,
        // We need to set this explicitly, since the cache will not update on old data
        updatedAt: new Date().toISOString(),
      })

      // This will run asynchonously. The cache will be updated with the new entity
      // when the API call is successful in another hook.
      return apiUpdate(entityId, resolved)
    }
  }

  const useEntityChildren = (props: {
    parentId?: string | null
    filter?: EntityType | FilterFunc
    // skip the query
    skip?: boolean
    defaultSort?: boolean
    includeSys?: boolean
    // Poll every x ms
    pollingInterval?: number
  }) => {
    const { filter, includeSys } = props
    const { entityCache } = useGraphCacheStore()

    const status = graphHooks.useEntityChildren(props)

    // We want to inject the cached items that are newer into the result

    // Get all the cached entries that have the same parent ID and are not in the apiEntries
    const cachedChildren = Array.from(entityCache.values())
    const candidateEntities = [...status.entities, ...cachedChildren].filter(
      childFilter({ parentId: props.parentId, filter, includeSys })
    )
    const seenIds = new Set<string>()

    const entities = candidateEntities
      .map(entity => {
        if (seenIds.has(entity.id)) {
          return null
        }
        seenIds.add(entity.id)
        const cached = entityCache.get(entity.id)
        const useCache = cached && cached.updatedAt > entity.updatedAt
        return useCache ? cached : entity
      })
      .filter(Boolean)

    // During RTK Query refetch (e.g., after createEntity cache invalidation),
    // isSuccess temporarily becomes false even though we have cached data.
    // Keep it true during refetch (not initial load) when cached entities are available.
    const isSuccess = status.isSuccess || (status.isFetching && entities.length > 0)

    return {
      ...status,
      entities,
      isSuccess,
    }
  }

  const useEntityCreate = <TEntityType extends Entity>() => {
    const apiCreate = graphHooks.useEntityCreate<TEntityType>()
    const { entityCache, upsertCache } = useGraphCacheStore()
    const { userId } = useCurrentUser()

    /**
     * `optimistic` (default true): when the entity carries a client-generated id, resolve at once with a
     * temporary entity while the server create runs in the background. Pass `optimistic: false` when the
     * caller opens the entity (e.g., navigates into its editor) — the editor's collab/presence room
     * registration is ACL-checked against the stored entity, so it must not run before the create lands.
     */
    const createFn = async <TCreate extends Entity = TEntityType>(props: {
      entity: TCreate
      parent: Persisted<Entity> | null
      isPrivate?: boolean
      optimistic?: boolean
    }) => {
      const { optimistic = true, ...createProps } = props
      const { entity } = createProps

      const resultPromise = apiCreate.createEntity(createProps)

      if (entity.id && optimistic) {
        const temporaryEntity = {
          ...entity,
          userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as unknown as Persisted<TCreate>
        // If the entity has an ID, we cache it immediately update it
        upsertCache(temporaryEntity)
        // Immediately return the temporary entity
        invariant(isPersisted(temporaryEntity), 'Temporary entity must be persisted')
        return temporaryEntity
      }
      const result = await resultPromise
      if (result) {
        entityCache.set(result.id, result)
      }

      return result
    }

    return {
      ...apiCreate,
      createEntity: createFn,
    }
  }

  const useEntitiesById = (ids: string[]) => {
    const status = graphHooks.useEntitiesById(ids)
    const { entityCache, upsertCache } = useGraphCacheStore()

    useEffect(() => {
      if (!status.isSuccess) {
        return
      }
      for (const entity of status.entities) {
        upsertCache(entity)
      }
    }, [status.entities, status.isSuccess, upsertCache])

    const entities = status.entities.map(entity =>
      getLatestEntry({ id: entity.id, candidate: entity, cache: entityCache })
    )
    const existingEntities = status.existingEntities.map(entity =>
      getLatestEntry({ id: entity.id, candidate: entity, cache: entityCache })
    )

    return {
      ...status,
      entities,
      existingEntities,
    }
  }

  const useEntityTrash = (entity?: Persisted<Entity>) => {
    const update = useEntityUpdate({ debounceDelay: 0 })

    const trashFn = () => {
      if (!entity) {
        return
      }

      /** Update will also update the cache (updatedAt is set by the cache layer) */
      update(entity.id, { trash: true })
    }
    return trashFn
  }

  const useEntityDelete = () => {
    const [deleteEntity, deleteEntityResult] = graphHooks.useEntityDelete()
    const { entityCache } = useGraphCacheStore()
    const deleteFn = async ({ entity }: { entity: Persisted<Entity> }) => {
      // Update the cache optimistically
      entityCache.delete(entity.id)
      return deleteEntity({ entity })
    }

    return [deleteFn, deleteEntityResult]
  }

  const useProfiles = (
    props: {
      userIds?: string[]
      includeAssistant?: boolean
    } = {}
  ) => {
    const status = graphHooks.useProfiles(props)
    return status
  }

  const useMembers = (entity?: Persisted<Entity>) => {
    const memberIds = [entity?.userId, ...(entity?.acls?.map(acl => acl.principal) ?? [])].filter(Boolean)

    const status = useProfiles({
      userIds: memberIds,
      includeAssistant: false,
    })
    return {
      ...status,
      members: status.profiles,
    }
  }

  const useEntityChild = <T extends Entity>(props: UseChildren) => {
    const status = graphHooks.useEntityChild<T>(props)

    return status
  }

  const useEntities = <T extends Entity>(props?: {
    /** Use only root entities, i.e., entities that have no parents */
    root?: boolean
    /** Load only partial */
    partial?: boolean
    showTrash?: boolean
    filter?: ElementType | FilterFunc
    /** Load owned entities */
    owned?: boolean
    // polling interval (in ms)
    pollingInterval?: number
  }) => {
    const status = graphHooks.useEntities<T>(props)

    const { entityCache, upsertCacheEntries } = useGraphCacheStore()

    useEffect(() => {
      if (!status.isSuccess) {
        return
      }
      upsertCacheEntries(status.entities)
    }, [status.entities, status.isSuccess, upsertCacheEntries])

    // Get all the root entities from the cache
    const cachedRootEntities = Array.from(entityCache.values())
      .filter(e => !e.parentId)
      .filter(e => includeElement(e, props?.filter))

    const candidateEntities = [...status.entities, ...cachedRootEntities]

    const seenIds = new Set<string>()
    const entities: Persisted<Entity>[] = []
    for (const entity of candidateEntities) {
      if (seenIds.has(entity.id)) {
        continue
      }
      seenIds.add(entity.id)

      const latest = getLatestEntry({ id: entity.id, candidate: entity, cache: entityCache })
      if (!latest) {
        continue
      }
      if (!props?.showTrash && latest.trash) {
        continue
      }
      entities.push(latest)
    }

    return { ...status, entities }
  }

  const useEntityClone = () => {
    const { cloneEntity, cloneEntityStatus } = graphHooks.useEntityClone()
    const { upsertCache } = useGraphCacheStore()

    const clone = async <T extends Persisted<Entity> = Persisted<Entity>>(props: {
      entity: T
      updatedProps: Partial<T>
    }) => {
      const result = await cloneEntity(props)
      if (result) {
        upsertCache(result)
      }
      return result
    }

    return { cloneEntity: clone, cloneEntityStatus }
  }

  /** Higher-order convenience hooks */
  const useEntityClips = (sourceId: string) => {
    const { edgeEntities, isLoading: isLoadingEdges } = graphHooks.useEdgeEntities<Entity, Audio>({
      sourceId,
      relation: 'hasRecording',
    })
    const { entities: childClips, isLoading } = graphHooks.useEntityChildren<Audio>({
      parentId: sourceId,
      filter: isAudio,
      includeSys: true,
    })
    const relatedClips = [...childClips, ...edgeEntities.map(({ target }) => target)]

    return { entityClips: relatedClips, isLoading: isLoading || isLoadingEdges }
  }

  return {
    ...graphHooks,
    useEntityClone,
    useEntityCreate,
    useEntity,
    useEntityUpdate,
    useEntityChildren,
    useEntitiesById,
    useMembers,
    useEntityChild,
    useEntityTrash,
    useEntityDelete,
    useEntities,
    usePastCollaborators,
    /** HOC */
    useEntityClips,
  }
}
